import {
  faCompressAlt,
  faDownload,
  faExpandAlt,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { DatePicker } from '../../components';
import Roles, {
  AccessAssignmentRequestCountEndpointPolicy,
  includesRoles,
} from 'constants/Roles';
import {
  BarSection,
  BarWrapper,
  CustomInput,
  DateFormat,
  StyledAuthorizedButton,
} from '../../components/filterBars';
import BudgetFilterPicker from './BudgetFilterPicker';
import { useBudgetFilters } from './useBudgetFilters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAssignmentRequestsGetCounts } from 'api/assignment-requests/assignment-requests';
import { useInternalDeliveryOverviewGetInternalDeliveryOverviewExportHook } from 'api/internal-delivery-overview/internal-delivery-overview';
import { RequestStatus, useApiCallFn } from 'hooks/useApiCallFn';
import { downloadBlob } from 'utils/file-utils';
import { formatDate } from 'utils/date-helpers';
import themeSizes from 'constants/Sizes';
import Modal from 'components/Modal';
import InternalDeliveryOverviewTable from './table/InternalDeliveryOverviewTable';
import { useState } from 'react';
import TextButton from 'components/inputs/TextButton';
import useAuthenticationState from 'contexts/authentication/useAuthentication';
import useMe from 'contexts/authentication/useMe';

const MyLabel = styled.label`
  margin-right: 5px;
`;

const FullScreenWrapper = styled.div`
  width: 95vw;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const DatePickerContainer = styled.div``;

const SubBar = styled(BarWrapper)`
  justify-content: flex-start;
`;

const RightBarSection = styled(BarSection)`
  margin-left: auto;
`;

const InternalDeliveryOverviewFilterBar = () => {
  const [showInFS, setShowInFS] = useState(false);
  const {
    state: { isAuthenticated },
  } = useAuthenticationState();
  const me = useMe();

  const countData = useAssignmentRequestsGetCounts({
    query: {
      staleTime: 15000,
      refetchInterval: 60000,
      enabled:
        isAuthenticated &&
        me !== null &&
        includesRoles(
          AccessAssignmentRequestCountEndpointPolicy.allow,
          me.roles
        ),
    },
  });
  const {
    state: { selectedDate: date, startDate, endDate },
    setState,
    selectedFilterView,
    setSelectedFilterView,
  } = useBudgetFilters();

  const handleChangeDate = (
    newDate: Date | [Date | null, Date | null] | null
  ) => {
    if (newDate) {
      if (!Array.isArray(newDate)) {
        setState((prev) => ({
          ...prev,
          selectedDate: newDate,
        }));
      }
    }
  };

  const getInternalDeliveryOverviewExport =
    useInternalDeliveryOverviewGetInternalDeliveryOverviewExportHook();
  const exportDataCall = useApiCallFn((from: Date, to: Date) =>
    getInternalDeliveryOverviewExport({ from, to })
  );

  const handleExport = async () => {
    const [result, error] = await exportDataCall.run(startDate, endDate);

    if (error) {
      return;
    }

    if (result) {
      downloadBlob(
        result,
        `InternleveransUnderlag_${formatDate(startDate)}_${formatDate(endDate)}.xlsx`
      );
    }
  };

  if (showInFS) {
    return (
      <Modal bgColor="black" fullscreen>
        <FullScreenWrapper>
          <TextButton
            style={{ marginLeft: 'auto', background: 'black' }}
            onClick={() => setShowInFS(false)}
          >
            <FontAwesomeIcon
              color="white"
              icon={faCompressAlt}
              fontSize={themeSizes.font.large}
            />
          </TextButton>
          <InternalDeliveryOverviewTable onlyTotals />
        </FullScreenWrapper>
      </Modal>
    );
  }

  return (
    <>
      <BarWrapper>
        <BarSection>
          <MyLabel>Visa:</MyLabel>
          <DatePickerContainer>
            <DatePicker
              calendarStartDay={1}
              customInput={
                <CustomInput
                  date={date}
                  dateFormat={
                    `${
                      selectedFilterView === 'week' ? 'week' : 'date'
                    }` as DateFormat
                  }
                />
              }
              dateFormat="yyyy-MM-dd"
              locale="sv"
              onChange={(d) => handleChangeDate(d)}
              populateHolidays
              selected={date}
              showYearPicker={selectedFilterView === 'year'}
              showMonthYearPicker={selectedFilterView === 'month'}
              showWeekNumbers
              isWeekSelect={selectedFilterView === 'week'}
              shouldCloseOnSelect
            />
          </DatePickerContainer>
        </BarSection>

        <BudgetFilterPicker
          onSelect={(select: DateFormat) => setSelectedFilterView(select)}
          selected={selectedFilterView}
        />

        <RightBarSection>
          <span>
            Nycklar för inventering:{' '}
            {countData.data?.numberOfKeysInStorage ?? 0}
          </span>
        </RightBarSection>
      </BarWrapper>
      {selectedFilterView === 'week' && (
        <SubBar>
          <StyledAuthorizedButton
            onClick={handleExport}
            disabled={exportDataCall.status === RequestStatus.Fetching}
            roles={[
              Roles.Annons,
              Roles.AnnonsAdmin,
              Roles.Admin,
              Roles.SuperAdmin,
              Roles.Riddermark,
            ]}
          >
            {exportDataCall.status === RequestStatus.Fetching ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} onClick={handleExport} />
                Hämta export
              </>
            )}
          </StyledAuthorizedButton>
          <TextButton
            style={{ marginLeft: 'auto', background: 'black' }}
            onClick={() => setShowInFS(true)}
          >
            <FontAwesomeIcon
              color="white"
              icon={faExpandAlt}
              fontSize={themeSizes.font.large}
            />
          </TextButton>
        </SubBar>
      )}
    </>
  );
};

export default InternalDeliveryOverviewFilterBar;
